import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const message = 'Hi, I would like to know more about Sara Realty services.';
    window.open(`https://wa.me/919715472526?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-primary text-white py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-5xl md:text-6xl font-medium mb-6">Get in Touch</h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
              Ready to start your real estate journey? Contact us today and let's make your property goals a reality.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-medium text-primary mb-6">
                  Let's Talk
                </h2>
                <p className="text-lg text-[#57534E] leading-relaxed mb-8">
                  Whether you're looking to buy, sell, or invest in real estate, our team is here to help you every step of the way.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center text-primary flex-shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-primary mb-1">Address</h3>
                    <p className="text-[#57534E]">Sakthi Enclave, No:4, 5th Main Road, Thilaiganganagar, Nanganallur, Chennai - 600 061</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center text-primary flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-primary mb-1">Phone</h3>
                    <p className="text-[#57534E]">+91 97154 72526</p>
                    <p className="text-[#57534E]">+91 63808 03026</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center text-primary flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-primary mb-1">Email</h3>
                    <p className="text-[#57534E]">sararealtychennai@gmail.com</p>
                  </div>
                </div>
              </div>

              <Button
                data-testid="whatsapp-contact-button"
                onClick={handleWhatsAppContact}
                className="w-full md:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white h-12 px-8 rounded-sm font-medium"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Connect via WhatsApp
              </Button>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="bg-white p-8 border border-stone-200 rounded-sm"
            >
              <h2 className="font-heading text-2xl font-medium text-primary mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-primary mb-2">
                    Your Name *
                  </label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-12 bg-white border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20 rounded-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-primary mb-2">
                    Your Email *
                  </label>
                  <Input
                    id="email"
                    data-testid="contact-email-input"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12 bg-white border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20 rounded-sm"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-primary mb-2">
                    Your Phone *
                  </label>
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    placeholder="+91 XXXXX XXXXX"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="h-12 bg-white border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20 rounded-sm"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-primary mb-2">
                    Your Message *
                  </label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    placeholder="Tell us how we can help you..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="bg-white border-stone-200 focus:border-primary focus:ring-1 focus:ring-primary/20 rounded-sm"
                  />
                </div>
                <Button
                  data-testid="submit-contact-button"
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-sm font-medium"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}